<template>
  <v-btn
    v-tooltip="'Importar Leituras'"
    icon="fa-regular fa-file-import"
    color="primary"
    :loading="loader.loading"
    @click="open"
  />
  <v-dialog v-model="dialog" persistent max-width="560px">
    <v-form ref="form" @submit.prevent="submit">
      <v-card>
        <v-toolbar color="primary" dark class="pl-6">
          <div class="d-flex flex-column">
            <span class="text-h6">Importar Leituras</span>
            <span class="text-caption">{{ event.title }}</span>
          </div>
        </v-toolbar>
        <v-card-text class="d-flex flex-column ga-4">
          <div class="text-body-2">
            Envie um arquivo CSV (separado por vírgula ou ponto e vírgula, até 20 MB) com as colunas
            <strong>id</strong> (ID do ingresso, código de barras ou CPF) e <strong>timestamp</strong>
            (horário da leitura no formato ISO 8601, ex.: 2026-10-04T20:30:00-03:00).
            Ingressos que já possuem leitura são mantidos, então o mesmo arquivo pode ser importado novamente.
          </div>
          <v-file-input
            v-model="file"
            accept=".csv,text/csv"
            :show-size="false"
            label="Arquivo CSV"
            variant="outlined"
            density="compact"
            hide-details="auto"
            prepend-icon=""
            prepend-inner-icon="fa-regular fa-file-csv"
            :disabled="loader.loading"
            :rules="[(f: File | null) => !!f || 'Selecione um arquivo para importar.']"
          />
          <v-text-field
            v-model="origin"
            label="Origem"
            placeholder="Importação - CSV"
            persistent-placeholder
            variant="outlined"
            density="compact"
            hide-details="auto"
            maxlength="255"
            :disabled="loader.loading"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="red darken-1"
            variant="text"
            :disabled="loader.loading"
            @click="dialog = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="green darken-1"
            variant="text"
            type="submit"
            :loading="loader.loading"
          >
            Importar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script lang="ts" setup>
import { ref, useTemplateRef } from 'vue'
import type { VForm } from 'vuetify/lib/components'
import ReadingService from '@/services/ReadingService'
import { describeImportSummary, readImportSummary } from '@/helpers/readingImport'
import { useLoader } from '@/stores/loader'
import { useSnackBar } from '@/stores/snackbar'
import Event from '@/types/Event'

const snackbar = useSnackBar()
const loader = useLoader()

const { event } = defineProps<{ event: Event }>()
const emits = defineEmits<(e: 'import') => void>()

const form = useTemplateRef<VForm>('form')

const dialog = ref(false)
const file = ref<File | null>(null)
const origin = ref('')

const service = new ReadingService()

function open() {
  file.value = null
  origin.value = ''
  dialog.value = true
}

function downloadReport(content: string) {
  const url = URL.createObjectURL(new Blob(['\ufeff', content], { type: 'text/csv' }))
  const link = document.createElement('a')

  link.href = url
  link.download = `relatorio_importacao_leituras_${(file.value?.name ?? 'leituras').replace(/\.[^.]+$/, '')}.csv`
  link.click()

  URL.revokeObjectURL(url)
}

async function submit() {
  await form.value?.validate()

  if (!form.value?.isValid || !file.value) return

  try {
    loader.increment()

    const response = await service.import(event.id, file.value, origin.value)
    const summary = readImportSummary(response.headers)

    downloadReport(response.data)

    const failed = summary.alreadyRead + summary.notFound + summary.exceeding + summary.invalidTimestamp

    snackbar.show(
      `Importação concluída: ${describeImportSummary(summary)} O relatório foi baixado.`,
      failed > 0 ? 'warning' : 'success',
      8000,
    )

    dialog.value = false
    emits('import')
  } catch (error) {
    console.error('Error importing readings:', error)
  } finally {
    loader.decrement()
  }
}
</script>
